import { z } from "zod";
/**
 * JOB KONTRATI — projenin en kritik tek dosyası.
 *
 * İstemci asla ham komut göndermez; yalnızca burada tanımlı bir job id'si ve
 * şemadan geçen parametreler gönderir. Sunucu doğrular, agent aynı şemayla
 * tekrar doğrular. "shell çalıştır" diye bir job YOKTUR ve eklenmeyecektir.
 *
 * ONAY POLİTİKASI — `confirm: "totp"` yalnızca GERİ ALINAMAZ işlerde:
 *   Play yüklemesi, kalıcı silme, askıya alma, toplu bildirim.
 *
 * Geri alınabilir işler (OTA yayını → republish, servis/bot restart → saniyeler,
 * bot deploy → önceki sürüm) tek tıkla çalışır. İki gerekçe:
 *   1. Kurtarma ve acil eylemler önüne kod bariyeri koymak zararlıdır — arıza
 *      anında hız gerekir (ota.rollback, bot.player.stopAll bu yüzden "click").
 *   2. Her şeye kod sorulursa kod sormak anlamını yitirir; kullanıcı refleksle
 *      girer ve asıl tehlikeli işte de düşünmeden geçer.
 */
export const JOB_KINDS = ["task", "process"];
/** Yıkıcılık seviyesi → istemcide istenecek onay. */
export const CONFIRM_LEVELS = ["none", "click", "totp"];
function defineJob(def) {
    return def;
}
// ---------------------------------------------------------------------------
// Ortak parametre parçaları
// ---------------------------------------------------------------------------
const appId = z.enum(["movieport", "raadiocast", "stockanalyzer", "raadiotr", "dailyguess", "cinderpath"]);
const otaChannel = z.enum(["production", "beta", "preview"]);
const releaseNotes = z.object({ tr: z.string().min(1), en: z.string().min(1) });
const playTrack = z.enum(["internal", "alpha", "beta", "production"]);
// ---------------------------------------------------------------------------
// 1. Sürüm & yayın öncesi kontrol (uygulamadan bağımsız)
// ---------------------------------------------------------------------------
/**
 * Yayın öncesi zorunlu kapı. Şunları karşılaştırır:
 *   app.json version · app.json android.versionCode · build.gradle versionCode
 *   build.gradle versionName · runtimeVersion · git worktree temizliği
 * Raadiocast'ta bilinen bir kayma var (app.json 26 ↔ gradle 27) — bu job onu yakalar.
 */
export const versionCheck = defineJob({
    id: "release.version.check",
    title: "Sürüm tutarlılık kontrolü",
    kind: "task",
    capability: "git",
    confirm: "none",
    estimate: "birkaç saniye",
    params: z.object({ app: appId }),
});
/**
 * Değişiklik analizi: OTA yeterli mi, yoksa native build şart mı?
 * package.json bağımlılıkları, app.json native alanları ve android/ altındaki
 * değişikliklere bakar. runtimeVersion değiştiyse OTA'yı KİLİTLER.
 */
export const releasePlan = defineJob({
    id: "release.plan",
    title: "Değişiklik analizi (OTA mı, build mi?)",
    kind: "task",
    capability: "git",
    confirm: "none",
    estimate: "birkaç saniye",
    params: z.object({ app: appId, sinceRef: z.string().optional() }),
});
export const versionBump = defineJob({
    id: "release.version.bump",
    lock: "workspace",
    title: "Sürüm yükselt",
    kind: "task",
    capability: "git",
    confirm: "click",
    warning: "build.gradle ve app.json birlikte güncellenir, sonra commit atılır.",
    params: z.object({
        app: appId,
        semver: z.enum(["patch", "minor", "major"]).or(z.string().regex(/^\d+\.\d+\.\d+$/)),
        bumpVersionCode: z.boolean().default(true),
        /** Elle versionCode (geri alma ya da atlama için); verilirse bumpVersionCode yok sayılır. */
        versionCode: z.number().int().positive().optional(),
        /** Sabit runtimeVersion (OTA uyumluluğu). Değişirse eski kurulumlar yeni OTA'ları almaz. */
        runtimeVersion: z.string().regex(/^[0-9A-Za-z][0-9A-Za-z.+-]{0,39}$/).optional(),
        commit: z.boolean().default(true),
    }),
});
// ---------------------------------------------------------------------------
// 2. Build
// ---------------------------------------------------------------------------
export const gradleBuild = defineJob({
    id: "build.gradle",
    lock: "workspace",
    title: "Yerel derleme (gradlew)",
    kind: "task",
    capability: "build.gradle",
    confirm: "click",
    estimate: "5-15 dk",
    warning: "Gradle daemon 2-4 GB RAM kullanır. Sürüm tutarlılık kontrolü geçmeden çalıştırma.",
    params: z.object({
        app: appId,
        task: z.enum(["bundleRelease", "assembleRelease"]).default("bundleRelease"),
        clean: z.boolean().default(false),
        /** Yalnızca kendi yayın betiği olan uygulamalarda: betiğe üretim argümanlarını geçir. */
        production: z.boolean().default(false),
    }),
});
export const easBuild = defineJob({
    id: "build.eas",
    lock: "workspace",
    title: "Bulut derleme (EAS)",
    kind: "task",
    capability: "build.eas",
    confirm: "click",
    estimate: "kuyruğa bağlı, 15-45 dk",
    params: z.object({
        app: appId,
        profile: z.enum(["production", "beta", "preview", "development"]),
    }),
});
// ---------------------------------------------------------------------------
// 3. OTA & web
// ---------------------------------------------------------------------------
export const otaPublish = defineJob({
    id: "ota.publish",
    lock: "workspace",
    title: "OTA yayınla",
    kind: "task",
    capability: "ota.publish",
    confirm: "click",
    estimate: "1-3 dk",
    warning: "Yayınlanan güncelleme mevcut kullanıcılara gider. runtimeVersion uyuşmazsa uygulama çöker.",
    params: z.object({
        app: appId,
        channel: otaChannel,
        message: z.string().min(1).max(200),
    }),
});
export const otaRollback = defineJob({
    id: "ota.rollback",
    title: "OTA geri al (önceki update'i yeniden yayınla)",
    kind: "task",
    capability: "ota.publish",
    confirm: "click",
    estimate: "1-2 dk",
    params: z.object({
        app: appId,
        channel: otaChannel,
        updateGroupId: z.string().min(1),
    }),
});
export const webDeploy = defineJob({
    id: "web.deploy",
    lock: "workspace",
    title: "Web sürümünü yayınla",
    kind: "task",
    capability: "web.deploy",
    confirm: "click",
    estimate: "2-5 dk",
    params: z.object({ app: appId }),
});
export const webRollback = defineJob({
    id: "web.rollback",
    title: "Web sürümünü geri al",
    kind: "task",
    capability: "web.deploy",
    confirm: "click",
    estimate: "saniyeler",
    params: z.object({ app: appId, deploymentId: z.string().min(1) }),
});
/**
 * Web + mobil OTA senkron yayın (MoviePort'un asıl kullanım senaryosu).
 * Job grubu olarak yürür: export → web deploy → OTA. Sıra bilinçlidir; web'in geri
 * alınması saniyeler sürer, OTA'nınki yeni bir yayın gerektirir → riskli olan sonda.
 * OTA adımı patlarsa web otomatik geri alınır.
 */
export const releaseSync = defineJob({
    id: "release.sync",
    lock: "workspace",
    title: "Web + Mobil OTA birlikte yayınla",
    kind: "task",
    capability: "ota.publish",
    confirm: "click",
    estimate: "5-10 dk",
    warning: "İki hedef tek yayın olarak gider. OTA adımı başarısız olursa web otomatik geri alınır.",
    params: z.object({
        app: appId,
        channel: otaChannel.default("production"),
        notes: releaseNotes,
        rollbackWebOnOtaFailure: z.boolean().default(true),
    }),
});
// ---------------------------------------------------------------------------
// 4. Google Play
// ---------------------------------------------------------------------------
export const playUpload = defineJob({
    id: "play.upload",
    lock: "workspace",
    title: "Play Console'a yükle ve yayınla",
    kind: "task",
    capability: "play.publish",
    confirm: "totp",
    estimate: "2-5 dk",
    warning: "Production track'e yükleme geri alınamaz; yalnızca rollout durdurulabilir.",
    params: z.object({
        app: appId,
        artifactId: z.string().min(1),
        track: playTrack,
        rolloutPercent: z.number().min(0).max(100).default(100),
        // Play sürüm notu istemiyor; boş bırakılan dil gönderilmez.
        notes: z
            .object({ tr: z.string().default(""), en: z.string().default("") })
            .default({ tr: "", en: "" }),
    }),
});
export const playRolloutUpdate = defineJob({
    id: "play.rollout.update",
    title: "Kademeli yayın oranını değiştir",
    kind: "task",
    capability: "play.publish",
    confirm: "click",
    params: z.object({
        app: appId,
        track: playTrack,
        rolloutPercent: z.number().min(0).max(100),
        halt: z.boolean().default(false),
    }),
});
// ---------------------------------------------------------------------------
// 5. Dev / test sunucusu (process job)
// ---------------------------------------------------------------------------
/**
 * "Test sunucusu" düğmesi bunu çağırır. Uzun ömürlü süreçtir: başlat/durum/durdur.
 * Agent stdout'tan portu ayrıştırıp `process.ready` ile URL bildirir; web konsolda
 * tıklanabilir localhost linkine dönüşür (mobilde yalnızca "PC'de açık" durumu).
 */
export const devServerStart = defineJob({
    id: "dev.server.start",
    title: "Test sunucusunu başlat",
    kind: "process",
    capability: "dev.server",
    confirm: "none",
    params: z.object({
        app: appId,
        mode: z.enum(["web", "native", "backend"]).default("web"),
        clearCache: z.boolean().default(false),
    }),
});
export const devServerStop = defineJob({
    id: "dev.server.stop",
    title: "Test sunucusunu durdur",
    kind: "task",
    capability: "dev.server",
    confirm: "none",
    params: z.object({ app: appId, processJobId: z.string().uuid() }),
});
// ---------------------------------------------------------------------------
// 6. VPS servis ops
// ---------------------------------------------------------------------------
const SERVICES = ["stockapi", "wordulary", "postgresql", "postgrest", "nginx", "zapret"];
export const serviceStatus = defineJob({
    id: "vps.service.status",
    title: "Servis durumu",
    kind: "task",
    capability: "service.ops",
    confirm: "none",
    params: z.object({ service: z.enum(SERVICES).optional() }),
});
export const serviceRestart = defineJob({
    id: "vps.service.restart",
    title: "Servisi yeniden başlat",
    kind: "task",
    capability: "service.ops",
    confirm: "click",
    warning: "Canlı servis kesintiye uğrar.",
    params: z.object({ service: z.enum(SERVICES) }),
});
export const journalTail = defineJob({
    id: "vps.journal.tail",
    title: "Servis logu (canlı)",
    kind: "process",
    capability: "service.ops",
    confirm: "none",
    params: z.object({ service: z.enum(SERVICES), lines: z.number().min(10).max(2000).default(200) }),
});
export const hostMetrics = defineJob({
    id: "vps.metrics",
    title: "Sunucu kaynakları",
    kind: "task",
    capability: "host.metrics",
    confirm: "none",
    params: z.object({}),
});
export const backupVerify = defineJob({
    id: "vps.backup.verify",
    title: "Yedek tazeliğini doğrula",
    kind: "task",
    capability: "backup.verify",
    confirm: "none",
    params: z.object({ maxAgeHours: z.number().min(1).max(168).default(36) }),
});
// ---------------------------------------------------------------------------
// 7. Discord botları (RAADIO TR)
// ---------------------------------------------------------------------------
const PM2_APPS = ["raadiotr", "raadiotr2", "lavalink"];
/**
 * Kök context.md'de yazılı manuel teşhis prosedürünün otomatik hali:
 * pm2 → zapret → Discord gateway curl → Lavalink stats → bot /health.
 * Zincirin nerede koptuğunu ve önerilen kurtarma job'ını döner.
 */
export const botDiagnose = defineJob({
    id: "bot.diagnose",
    title: "Botu teşhis et",
    kind: "task",
    capability: "pm2.ops",
    confirm: "none",
    estimate: "~20 sn",
    params: z.object({}),
});
export const pm2Status = defineJob({
    id: "bot.pm2.status",
    title: "pm2 durumu",
    kind: "task",
    capability: "pm2.ops",
    confirm: "none",
    params: z.object({ process: z.enum(PM2_APPS).optional() }),
});
export const pm2Restart = defineJob({
    id: "bot.pm2.restart",
    title: "Süreci yeniden başlat",
    kind: "task",
    capability: "pm2.ops",
    confirm: "click",
    warning: "Çalan yayınlar kesilir.",
    params: z.object({ process: z.enum(PM2_APPS) }),
});
export const pm2LogTail = defineJob({
    id: "bot.pm2.logs",
    title: "Bot logu (canlı)",
    kind: "process",
    capability: "pm2.ops",
    confirm: "none",
    params: z.object({
        process: z.enum(PM2_APPS),
        errorsOnly: z.boolean().default(false),
    }),
});
export const botState = defineJob({
    id: "bot.state",
    title: "Canlı bot durumu (guild / kuyruk / çalan)",
    kind: "task",
    capability: "bot.control",
    confirm: "none",
    params: z.object({ process: z.enum(["raadiotr", "raadiotr2"]).optional() }),
});
export const botPlayerAction = defineJob({
    id: "bot.player.action",
    title: "Oynatıcı kontrolü",
    kind: "task",
    capability: "bot.control",
    confirm: "click",
    warning: "Gerçek kullanıcıların dinlediği yayını etkiler.",
    params: z.object({
        process: z.enum(["raadiotr", "raadiotr2"]),
        guildId: z.string().min(1),
        action: z.enum(["pause", "resume", "skip", "stop", "leave"]),
        volume: z.number().min(0).max(200).optional(),
    }),
});
export const botStopAll = defineJob({
    id: "bot.player.stopAll",
    title: "ACİL: tüm oynatıcıları durdur",
    kind: "task",
    capability: "bot.control",
    confirm: "click",
    warning: "Tüm sunucularda yayın kesilir. Yalnızca acil durumda kullan.",
    params: z.object({ process: z.enum(["raadiotr", "raadiotr2", "both"]).default("both") }),
});
export const botDeploy = defineJob({
    id: "bot.deploy",
    title: "Bot kodunu VPS'e gönder (SFTP) ve yeniden başlat",
    kind: "task",
    capability: "sftp.deploy",
    confirm: "click",
    estimate: "1-3 dk",
    warning: "data/ klasörü git'te izleniyor — dizin kopyalama yapılmaz, yalnızca dosya bazlı yükleme.",
    params: z.object({
        files: z.array(z.string().min(1)).min(1),
        restart: z.array(z.enum(PM2_APPS)).default(["raadiotr", "raadiotr2"]),
    }),
});
export const botDeployCommands = defineJob({
    id: "bot.commands.deploy",
    title: "Slash komutlarını yayınla",
    kind: "task",
    capability: "bot.control",
    confirm: "click",
    warning: "Discord tarafında komut tanımlarını günceller.",
    params: z.object({ guildIds: z.array(z.string()).optional(), global: z.boolean().default(false) }),
});
export const radioStationsWrite = defineJob({
    id: "bot.stations.write",
    title: "Radyo istasyonlarını güncelle",
    kind: "task",
    capability: "sftp.deploy",
    confirm: "click",
    warning: "İki bot süreci aynı data/*.json dosyasını kullanır; yazım tek yazıcı (agent) üzerinden yapılır ve ardından botlara yeniden yükleme sinyali gider.",
    params: z.object({
        scope: z.enum(["global", "guild"]),
        guildId: z.string().optional(),
        stations: z.array(z.object({
            name: z.string().min(1),
            url: z.string().url(),
            logo: z.string().url().optional(),
            tags: z.array(z.string()).default([]),
        })),
    }),
});
export const radioHealthScan = defineJob({
    id: "bot.stations.healthScan",
    title: "İstasyon yayın sağlığı taraması",
    kind: "task",
    capability: "bot.control",
    confirm: "none",
    estimate: "istasyon sayısına göre 2-10 dk",
    params: z.object({ timeoutMs: z.number().min(1000).max(30000).default(8000) }),
});
// ---------------------------------------------------------------------------
// Mağaza görselleri
// ---------------------------------------------------------------------------
/**
 * Play Store ekran görüntüleri + feature graphic, dil başına (packages/store-assets).
 * capture: uygulamanın web derlemesini demo veriyle telefon boyutunda çeker.
 * render:  ham ekranlardan tasarım şablonuyla mağaza görsellerini üretir.
 * Yalnızca yerel dosya üretir; Play'e yükleme bu işin parçası DEĞİL.
 */
export const storeAssetsGenerate = defineJob({
    id: "store.assets.generate",
    lock: "workspace",
    title: "Mağaza görsellerini üret",
    kind: "task",
    capability: "store.assets",
    confirm: "none",
    estimate: "dil başına ~1 dk (web derlemesi gerekirse +3 dk)",
    params: z.object({
        app: appId,
        steps: z.enum(["all", "capture", "render"]).default("all"),
        /** Boş → project.json'daki tüm diller. */
        langs: z.array(z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/)).max(40).default([]),
        rebuild: z.boolean().default(false),
    }),
});
const storeUploadParams = z.object({
    app: appId,
    /** Boş → üretilmiş tüm diller. Uygulama dil kodu (tr, en…); Play kodu project.json'dan gelir. */
    langs: z.array(z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/)).max(40).default([]),
    phone: z.boolean().default(true),
    tablet: z.boolean().default(true),
    featureGraphic: z.boolean().default(true),
    /** false → Play'de zaten görseli olan türlere dokunulmaz (elle yüklenenler korunur). */
    replaceExisting: z.boolean().default(false),
    /** true → Play'de sayfası olan dillerin metni de listing.json'a göre güncellenir. */
    syncListingTexts: z.boolean().default(true),
});
/**
 * Salt okuma önizleme: Play'de hangi dillerde kaç görsel silinip kaç yükleneceğini listeler.
 * Edit açılır ama kaydedilmeden silinir; mağaza sayfası değişmez.
 */
export const storeAssetsPreview = defineJob({
    id: "store.assets.preview",
    title: "Play görsel yüklemesini önizle",
    kind: "task",
    capability: "play.publish",
    confirm: "none",
    estimate: "dil başına birkaç saniye",
    params: storeUploadParams,
});
/**
 * store.assets.generate çıktısını Play mağaza sayfasına yükler. Tek edit; Play doğrulaması
 * geçmeden commit edilmez. Mağaza sayfası olmayan diller atlanır.
 */
export const storeAssetsUpload = defineJob({
    id: "store.assets.upload",
    title: "Mağaza görsellerini Play'e yükle",
    kind: "task",
    capability: "play.publish",
    confirm: "totp",
    estimate: "dil başına ~20 sn",
    warning: "Play'de sayfası olmayan diller listing.json metinleriyle açılır ve görselleri yüklenir. 'Mevcutları değiştir' seçiliyse var olan görseller silinir. Yayındaki mağaza sayfasına yansır.",
    params: storeUploadParams,
});
/** Konsol "Mağaza" sayfası: diller, üretilmiş görseller, metinler, bir dilin önizlemeleri. */
export const storeOverview = defineJob({
    id: "store.overview",
    title: "Mağaza durumunu oku",
    kind: "task",
    capability: "store.assets",
    confirm: "none",
    estimate: "birkaç saniye",
    params: z.object({ app: appId, lang: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/).optional() }),
});
const codePoints = (max) => z.string().refine((s) => [...s].length <= max, { message: `En fazla ${max} karakter` });
/** Bir dilin mağaza metnini listing.json'a yazar; Play'e store.assets.upload taşır. */
export const storeListingWrite = defineJob({
    id: "store.listing.write",
    title: "Mağaza metnini kaydet",
    kind: "task",
    capability: "store.assets",
    confirm: "none",
    estimate: "anında",
    params: z.object({
        app: appId,
        /** Play dil kodu (tr-TR, en-US, id…). */
        play: z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/),
        title: codePoints(30).pipe(z.string().min(1)),
        shortDescription: codePoints(80).pipe(z.string().min(1)),
        fullDescription: codePoints(4000).pipe(z.string().min(1)),
    }),
});
// ---------------------------------------------------------------------------
// Kayıt defteri
// ---------------------------------------------------------------------------
export const JOBS = {
    [versionCheck.id]: versionCheck,
    [releasePlan.id]: releasePlan,
    [versionBump.id]: versionBump,
    [gradleBuild.id]: gradleBuild,
    [easBuild.id]: easBuild,
    [otaPublish.id]: otaPublish,
    [otaRollback.id]: otaRollback,
    [webDeploy.id]: webDeploy,
    [webRollback.id]: webRollback,
    [releaseSync.id]: releaseSync,
    [playUpload.id]: playUpload,
    [playRolloutUpdate.id]: playRolloutUpdate,
    [devServerStart.id]: devServerStart,
    [devServerStop.id]: devServerStop,
    [serviceStatus.id]: serviceStatus,
    [serviceRestart.id]: serviceRestart,
    [journalTail.id]: journalTail,
    [hostMetrics.id]: hostMetrics,
    [backupVerify.id]: backupVerify,
    [botDiagnose.id]: botDiagnose,
    [pm2Status.id]: pm2Status,
    [pm2Restart.id]: pm2Restart,
    [pm2LogTail.id]: pm2LogTail,
    [botState.id]: botState,
    [botPlayerAction.id]: botPlayerAction,
    [botStopAll.id]: botStopAll,
    [botDeploy.id]: botDeploy,
    [botDeployCommands.id]: botDeployCommands,
    [radioStationsWrite.id]: radioStationsWrite,
    [radioHealthScan.id]: radioHealthScan,
    [storeAssetsGenerate.id]: storeAssetsGenerate,
    [storeAssetsPreview.id]: storeAssetsPreview,
    [storeAssetsUpload.id]: storeAssetsUpload,
    [storeOverview.id]: storeOverview,
    [storeListingWrite.id]: storeListingWrite,
};
/** Aynı uygulamada sıraya giren (klasöre yazan) iş tipleri. */
export const WORKSPACE_LOCKED_JOBS = Object.values(JOBS)
    .filter((d) => d.lock === "workspace")
    .map((d) => d.id);
export function getJob(id) {
    return JOBS[id];
}
/** Sunucu ve agent aynı doğrulamayı yapar — tek kaynak. */
export function parseJobParams(id, params) {
    const def = getJob(id);
    if (!def)
        throw new Error(`Bilinmeyen job tipi: ${id}`);
    return def.params.parse(params);
}
//# sourceMappingURL=jobs.js.map